import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { entityService } from '@/services/EntityService';
import type { DocumentEntity } from '@/types/Documents';
import type { RequestOptions } from '@/types/api';

export interface DocumentRetrievalResponse {
  documentId: string;
  filename: string;
  contentType: string;
  content: string;
}

export interface PdfFormItem {
  name: string;
  lin?: string;
  nsn?: string;
  quantity: number;
  unitPrice: number;
  tax?: number; // User-entered tax amount for reimbursement
  shipping?: number; // User-entered shipping amount for reimbursement
  loa?: string; // Line of Accounting from catalog item
  receiptDate?: string; // Per-item receipt date (YYYY-MM-DD) for fiscal year attribution (#458)
}

export interface PdfFormSignatures {
  individual?: string;
}

export interface EditableField {
  /** PDF field name (from form) */
  pdfFieldName: string;
  /** Data source key in payload */
  dataSource: string;
  /** Data type for validation */
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'array';
  /** Whether field is required */
  required: boolean;
  /** Current value from payload */
  currentValue: unknown;
  /** Default value if not provided */
  defaultValue?: unknown;
  /** Display label for UI */
  displayLabel: string;
  /** Display order in forms */
  displayOrder: number;
  /** Section/group name */
  section: string;
  /** Help text for users */
  helpText?: string;
  /** Placeholder text */
  placeholder?: string;
}

export interface DocumentPreviewRequest {
  documentType: string;
  payload: Record<string, unknown>;
}

export interface DocumentPreviewResponse {
  documentType: string;
  documentName: string;
  version: string;
  sections: string[];
  editableFields: EditableField[];
  /** The transformed payload that will be sent to the workflow */
  transformedPayload: Record<string, unknown>;
}

export interface PdfFormFillRequest {
  documentId: string;
  name: string;
  items?: PdfFormItem[];

  // Tier 1 enhancements - optional fields for SF1034
  certifyingOfficer?: {
    name?: string;
    title?: string;
  };
  organization?: {
    name?: string;
    location?: string;
  };
  transactionInfo?: {
    id?: string;
    timestamp?: string;
  };
  remarks?: string;
  signatures?: PdfFormSignatures;
}

export interface PdfFormFillResponse {
  documentId: string;
  // Stable URL to retrieve the generated document
  downloadUrl?: string;
  contentType: string;
  filename: string;
  documentNumber?: string | null;
  documentType?: string | null;
  downloadUrlExpiresAt?: string | null;
  createdAt?: string | null;
  /** Inline PDF as base64 — use to skip download round-trip when available */
  outputBuffer?: string;
  documentEntityId?: string | null;
  documentEntityDownloadUrl?: string | null;
}

export interface GenerateDocumentRequest {
  documentType: string;
  /** Pre-transformed payload in the document's native format (e.g., SF1034 format) */
  payload: Record<string, unknown>;
  options?: {
    skipStorage?: boolean;
    skipRegister?: boolean;
    metadata?: Record<string, unknown>;
  };
  /** Pre-assigned document number (non-expendable/durable series). */
  nonExpendableDocumentNumber?: string;
  /** Pre-assigned document number (expendable series). */
  expendableDocumentNumber?: string;
}

export interface GenerateDocumentResponse {
  documentId: string;
  downloadUrl?: string;
  contentType: string;
  filename: string;
  documentNumber?: string | null;
  documentType?: string | null;
  downloadUrlExpiresAt?: string | null;
  createdAt?: string | null;
  /** Inline PDF as base64 — use to skip download round-trip when available */
  outputBuffer?: string;
  documentEntityId?: string | null;
  documentEntityDownloadUrl?: string | null;
}

export interface SaveGeneratedDocumentInput {
  documentId: string;
  documentType: string;
  filename: string;
  downloadUrl: string;
  documentNumber?: string | null;
  transactionId?: string;
  transactionIdentifier?: string;
  memberId?: string;
  memberName?: string;
  downloadUrlExpiresAt?: string | null;
  source?: string;
  contentType?: string;
}

export class DocumentService {
  async retrieveDocument(documentId: string): Promise<DocumentRetrievalResponse> {
    const trimmedId = documentId.trim();
    if (!trimmedId) {
      throw new Error('documentId is required');
    }

    return httpClient.post<DocumentRetrievalResponse>(API_CONFIG.ENDPOINTS.DOCUMENT_RETRIEVAL, {
      documentId: trimmedId,
    });
  }

  async getDocumentPreview(request: DocumentPreviewRequest): Promise<DocumentPreviewResponse> {
    if (!request.documentType?.trim()) {
      throw new Error('documentType is required');
    }

    return httpClient.post<DocumentPreviewResponse>(API_CONFIG.ENDPOINTS.DOCUMENT_PREVIEW, {
      documentType: request.documentType.trim(),
      payload: request.payload,
    });
  }

  /**
   * Generate a document with a pre-transformed payload.
   * Use this when you have already transformed the payload (e.g., after user edits in preview modal).
   * This bypasses the automatic transformation that fillPdfForm does.
   */
  async generateDocument(
    request: GenerateDocumentRequest,
    requestOptions?: RequestOptions,
  ): Promise<GenerateDocumentResponse> {
    if (!request.documentType?.trim()) {
      throw new Error('documentType is required');
    }

    if (!request.payload || typeof request.payload !== 'object') {
      throw new Error('payload is required');
    }

    return httpClient.post<GenerateDocumentResponse>(
      API_CONFIG.ENDPOINTS.DOCUMENT_GENERATE,
      {
        documentType: request.documentType.trim(),
        payload: request.payload,
        options: request.options,
        ...(request.nonExpendableDocumentNumber
          ? { nonExpendableDocumentNumber: request.nonExpendableDocumentNumber }
          : undefined),
        ...(request.expendableDocumentNumber
          ? { expendableDocumentNumber: request.expendableDocumentNumber }
          : undefined),
      },
      requestOptions,
    );
  }

  async fillPdfForm(request: PdfFormFillRequest): Promise<PdfFormFillResponse> {
    const trimmedId = request.documentId.trim();
    const trimmedName = request.name.trim();

    if (!trimmedId) {
      throw new Error('documentId is required');
    }

    if (!trimmedName) {
      throw new Error('name is required');
    }

    return httpClient.post<PdfFormFillResponse>(API_CONFIG.ENDPOINTS.PDF_FORM_FILL, {
      documentId: trimmedId,
      name: trimmedName,
      items: request.items,
      // Include optional Tier 1 enhancement fields
      certifyingOfficer: request.certifyingOfficer,
      organization: request.organization,
      transactionInfo: request.transactionInfo,
      remarks: request.remarks,
      signatures: request.signatures,
    });
  }

  async saveGeneratedDocument(input: SaveGeneratedDocumentInput): Promise<DocumentEntity> {
    const identifier = this.buildDocumentIdentifier(
      input.documentNumber,
      input.documentType,
      input.documentId,
    );
    const payload = {
      entity_type: 'document',
      entity_identifier: identifier,
      display_name: identifier,
      entity_data: {
        document_type: input.documentType,
        document_number: input.documentNumber ?? null,
        filename: input.filename,
        content_type: input.contentType ?? 'application/pdf',
        generated_document_id: input.documentId,
        transaction_id: input.transactionId ?? null,
        transaction_identifier: input.transactionIdentifier ?? null,
        member_id: input.memberId ?? null,
        member_name: input.memberName ?? null,
        source: input.source ?? 'equip',
      },
      metadata: {
        source: input.source ?? 'equip',
      },
      status: 'available',
      client_reference: input.documentId,
    };

    const entity = await entityService.createEntity(payload);
    return entity as DocumentEntity;
  }

  /**
   * Link an existing document entity to a transaction by updating its entity_data.
   * This is used after document generation to make the document appear in the transaction log.
   */
  async linkDocumentToTransaction(documentEntityId: string, transactionId: string): Promise<void> {
    // First fetch the current document entity to get its entity_data
    const entity = await entityService.getEntity(documentEntityId, 'document');
    if (!entity) {
      throw new Error(`Document entity not found: ${documentEntityId}`);
    }

    // Update entity_data with transaction_id
    const currentEntityData = (entity.entity_data as Record<string, unknown>) ?? {};
    const updatedEntityData = {
      ...currentEntityData,
      transaction_id: transactionId,
    };

    await entityService.updateEntity(documentEntityId, {
      entity_data: updatedEntityData,
    });
  }

  /** Builds the actions-api download path for a generated document. */
  buildDownloadPath(generatedDocumentId: string): string {
    return `${API_CONFIG.ACTIONS_API_BASE_PATH}/documents/${generatedDocumentId}`;
  }

  resolveDownloadUrl(downloadUrl?: string | null): string | null {
    if (!downloadUrl) {
      return null;
    }
    return this.ensureAbsoluteUrl(downloadUrl);
  }

  resolveDocumentEntityUrl(entity: DocumentEntity | null | undefined): string | null {
    if (!entity) {
      return null;
    }
    const generatedDocumentId = entity.entity_data?.generated_document_id;
    if (typeof generatedDocumentId === 'string' && generatedDocumentId.length > 0) {
      return this.ensureAbsoluteUrl(this.buildDownloadPath(generatedDocumentId));
    }
    // Pre-cutover rows may carry a non-derivable value such as an inline data: URI.
    const legacyUrl = entity.entity_data?.download_url;
    return this.resolveDownloadUrl(typeof legacyUrl === 'string' ? legacyUrl : null);
  }

  isDocumentUrlExpired(entity: DocumentEntity | null | undefined): boolean {
    if (!entity) {
      return true;
    }
    const expiresAt = entity.entity_data?.download_url_expires_at;
    if (!expiresAt) {
      return false;
    }
    const expires = new Date(expiresAt).getTime();
    if (Number.isNaN(expires)) {
      return false;
    }
    return expires <= Date.now();
  }

  private buildDocumentIdentifier(
    documentNumber: string | null | undefined,
    documentType: string | null | undefined,
    documentId: string,
  ): string {
    const candidate = (documentNumber ?? '').trim();
    if (candidate.length > 0) {
      return candidate.slice(0, 240);
    }

    const typeSegment = (documentType ?? 'DOC').trim().toUpperCase() || 'DOC';
    const idSegment =
      documentId
        .replace(/[^A-Za-z0-9]/g, '')
        .slice(0, 8)
        .toUpperCase() || 'ID';

    return `${typeSegment}-${idSegment}`;
  }

  private ensureAbsoluteUrl(url: string): string {
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith('/')) {
      return `${API_CONFIG.BASE_URL}${trimmed}`;
    }
    return `${API_CONFIG.BASE_URL}/${trimmed}`;
  }

  /**
   * Save a document generation payload as an entity so the document can be
   * generated later (e.g. when the user skips document generation).
   */
  async saveDocumentPayload(input: {
    documentType: string;
    payload: Record<string, unknown>;
    transactionId?: string | null;
    documentNumber?: string | null;
    options?: Record<string, unknown> | null;
  }): Promise<{ id: string }> {
    const suffix = input.documentNumber ?? input.transactionId ?? crypto.randomUUID();
    const entityIdentifier = `DOCGEN-${input.documentType}-${suffix}`;

    return entityService.create({
      entity_type: 'document_generation_payload',
      entity_identifier: entityIdentifier,
      display_name: `Document Generation Payload • ${input.documentType}`,
      entity_data: {
        document_type: input.documentType,
        payload: input.payload,
        document_number: input.documentNumber ?? null,
        transaction_id: input.transactionId ?? null,
        options: input.options ?? null,
        non_expendable_document_number: input.documentNumber ?? null,
        expendable_document_number: null,
      },
      metadata: { source: 'skip-document-generation' },
      status: 'active',
      client_reference: `docgen-skip-${input.documentType}-${suffix}`,
    });
  }
}

export const documentService = new DocumentService();
